import React, { useState, useEffect } from 'react'

import {
  DeliveryIcon,
  PackageIcon,
  PickUpIcon,
  WarningOutlineIcon
} from '@toasttab/buffet-pui-icons'

import {
  DiningOptionBehavior,
  FulfillmentType
} from 'src/apollo/onlineOrdering'
import { useCart } from 'src/public/components/online_ordering/CartContext'
import { useFulfillment } from 'src/public/components/online_ordering/FulfillmentContext'
import { useValidateCartForShipping } from 'public/components/default_template/online_ordering/cart/useValidateCartForShipping'
import { useRestaurant } from 'src/shared/components/common/restaurant_context/RestaurantContext'
import Slider, { Option } from 'src/shared/components/common/slider/Slider'

import { PickupSection } from './PickupSection'
import { ShippingSection } from './ShippingSection'
import DeliverySection from './DeliverySection'
import { usePayment } from 'src/public/components/online_ordering/PaymentContext'
import { t } from 'src/translations'

enum FulfillmentOptions {
  Shipping = 'Shipping',
  Delivery = 'Delivery',
  Pickup = 'Pickup',
  DigitalDownload = 'Digital Download'
}

export const allFulfillmentOptions = (t: any) => {
  return {
    SHIPPING: {
      name: t('fulfillment-section.options.shipping'),
      icon: <PackageIcon />,
      value: FulfillmentOptions.Shipping
    } as Option,
    DELIVERY: {
      name: t('fulfillment-section.options.delivery'),
      icon: <DeliveryIcon />,
      position: 1,
      value: FulfillmentOptions.Delivery
    } as Option,
    PICKUP: {
      name: t('fulfillment-section.options.pickup'),
      icon: <PickUpIcon />,
      position: 2,
      value: FulfillmentOptions.Pickup
    } as Option,
    DIGITAL_DOWNLOAD: {
      name: t('fulfillment-section.options.digital-download'),
      icon: <PickUpIcon />,
      position: 3,
      value: FulfillmentOptions.DigitalDownload
    } as Option
  }
}

export const FulfillmentSectionV2 = () => {
  const [selectedFulfillment, setSelectedFulfillment] = useState<
    Option | undefined
  >()
  const { cart } = useCart()
  const { ecommSettings, loadingEcommSettings } = useRestaurant()
  const { updateCartFulfillment, loadingFulfillment } = useFulfillment()
  const [fulfillmentOptions, setFulfillmentOptions] = useState<Option[]>([])

  const {
    loadingValidateCart,
    validateCartError,
    shippingValid,
    isShippingInvalid
  } = useValidateCartForShipping()

  const { setTipAmount, tipAmount } = usePayment()

  useEffect(() => {
    if (
      selectedFulfillment &&
      selectedFulfillment.value === FulfillmentOptions.Pickup
    ) {
      updateCartFulfillment({
        fulfillmentType: FulfillmentType.Asap,
        diningOptionBehavior: DiningOptionBehavior.TakeOut
      })
    }
  }, [selectedFulfillment, updateCartFulfillment, cart?.order?.deliveryInfo])

  useEffect(() => {
    if (
      selectedFulfillment?.value !== FulfillmentOptions.Delivery &&
      tipAmount !== 0
    ) {
      setTipAmount(0)
    }
  }, [selectedFulfillment, tipAmount])

  useEffect(() => {
    const { SHIPPING, PICKUP, DELIVERY } = allFulfillmentOptions(t)

    const options = []
    let position = 0

    let initialValue
    const defaultFulfillmentType =
      ecommSettings?.fulfillmentConfig?.defaultFulfillmentType

    if (!ecommSettings || !ecommSettings?.fulfillmentConfig) {
      const shippingOption = { ...SHIPPING, position: position }
      options.push(shippingOption)
      initialValue ??= shippingOption

      position += 1
    }

    if (ecommSettings?.fulfillmentConfig?.shippingSettings?.enabled) {
      const allowedStates =
        ecommSettings?.fulfillmentConfig?.shippingSettings?.shippingRestrictions
          ?.alcohol?.allowedStates
      const hasAllowedStates =
        allowedStates !== null &&
        allowedStates !== undefined &&
        allowedStates.length > 0

      // If there are allowed states, keep shipping enabled so user can try a different address
      const shouldDisable = !shippingValid && !hasAllowedStates

      const shippingOption = {
        ...SHIPPING,
        position: position,
        disabled: shouldDisable
      }
      options.push(shippingOption)
      initialValue ??= shippingOption

      position += 1
    }

    if (ecommSettings?.fulfillmentConfig?.deliverySettings?.enabled) {
      const deliveryOption = { ...DELIVERY, position: position }
      options.push(deliveryOption)
      initialValue ??= deliveryOption
      position += 1
    }

    if (ecommSettings?.fulfillmentConfig?.pickupSettings?.enabled) {
      const pickupOption = { ...PICKUP, position: position }
      options.push(pickupOption)
      initialValue ??= pickupOption

      position += 1
    }

    // Override initial value based on defaultFulfillmentType if it's set and available
    if (defaultFulfillmentType && options.length > 0) {
      const defaultOption = options.find((option) => {
        switch (defaultFulfillmentType) {
          case 'SHIPPING':
            return option.value === FulfillmentOptions.Shipping
          case 'DELIVERY':
            return option.value === FulfillmentOptions.Delivery
          case 'PICKUP':
            return option.value === FulfillmentOptions.Pickup
          default:
            return false
        }
      })

      if (defaultOption && !defaultOption.disabled) {
        initialValue = defaultOption
      }
    }

    initialValue ??= { ...SHIPPING, position: position }

    // If the chosen initial value is disabled (e.g. shipping invalid), pick first selectable option
    const selectableOptions = options.filter((opt) => !opt.disabled)
    const valueToSet =
      selectableOptions.length > 0 && initialValue?.disabled
        ? selectableOptions[0]
        : initialValue

    setSelectedFulfillment((valueToSet ?? initialValue) as Option)
    setFulfillmentOptions(options)
  }, [
    ecommSettings,
    shippingValid,
    setSelectedFulfillment,
    setFulfillmentOptions
  ])

  if (loadingEcommSettings || !selectedFulfillment) {
    return null
  }

  return (
    <FulfillmentSectionView
      fulfillmentOptions={fulfillmentOptions}
      selectedFulfillmentType={selectedFulfillment}
      setValue={setSelectedFulfillment}
      loadingFulfillment={loadingFulfillment}
      loadingValidateCart={loadingValidateCart}
      isShippingInvalid={isShippingInvalid}
      invalidShippingMessage={
        validateCartError
          ? t('fulfillment-section.shipping-unavailable-error-validate')
          : t('fulfillment-section.shipping-unavailable-pickup-only')
      }
    />
  )
}

type FulfillmentSectionViewProps = {
  fulfillmentOptions: Option[]
  selectedFulfillmentType: Option
  setValue: (value: Option) => void
  loadingFulfillment: boolean
  loadingValidateCart?: boolean
  invalidShippingMessage?: string
  isShippingInvalid?: boolean
}

// Only exported for storybook purposes, FulfillmentSectionV2 should be used in the application
export const FulfillmentSectionView = ({
  fulfillmentOptions,
  selectedFulfillmentType,
  setValue,
  loadingFulfillment,
  loadingValidateCart = false,
  invalidShippingMessage,
  isShippingInvalid = false
}: FulfillmentSectionViewProps) => {
  return (
    <div>
      {fulfillmentOptions.length > 1 && (
        <Slider
          className='fulfillmentToggleRedesign'
          value={selectedFulfillmentType}
          onChange={setValue}
          themed={false}
          options={fulfillmentOptions}
          disabled={loadingFulfillment}
        />
      )}
      {isShippingInvalid && (
        <div className='fulfillmentSection-shippingUnavailable' role='alert'>
          <span className='shippingUnavailableIcon'>
            <WarningOutlineIcon aria-hidden />
          </span>
          <span>{invalidShippingMessage}</span>
        </div>
      )}
      {selectedFulfillmentType.value === FulfillmentOptions.Shipping && (
        <ShippingSection disableControls={loadingValidateCart} />
      )}

      {selectedFulfillmentType.value === FulfillmentOptions.Pickup && (
        <PickupSection />
      )}

      {selectedFulfillmentType.value === FulfillmentOptions.Delivery && (
        <DeliverySection />
      )}
    </div>
  )
}

export const Shipping = () => {
  return (
    <div>
      <div className='contactHeader'>{t('fulfillment-section.contact')}</div>
      <ShippingSection />
    </div>
  )
}
